import {
  IonCard,
  IonCardContent,
  IonHeader,
  IonItem,
  IonLabel,
  IonList,
  IonLoading,
  useIonViewDidEnter,
} from "@ionic/react";
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router";
import {
  getFieldsByGroup,
  WorkOrder,
  formatData,
  getAllFields,
} from "../models/workorders/WorkOrder";
import { decodeParam } from "../util/ApiHelper";
import OnlineStatus from "../components/OnlineStatus";
import { withAITracking } from "@microsoft/applicationinsights-react-js";
import { reactPlugin } from "../util/AppInsights";
import ApiError from "../components/ApiError";
import WorkOrderCustomFields from "../components/WorkOrderCustomFields";
import EmployeeContactField from "../components/EmployeeContactField";
import { isEmployeeField } from "../util/EmployeeContact";
import { TranslationMessagesContext } from "../util/Translations";
import { useWorkOrderStore } from "../store/wo-order";
import { useNetworkStatusStore } from "../store/network-status";
import { useShallow } from "zustand/react/shallow";
import useGetWorkOrder from "../hooks/useGetWorkOrder";
import useGetMetaData from "../hooks/work-order/useGetMetaData";
import useGetCustomValues from "../hooks/work-order/useGetCustomValues";
import useGetCustomFields from "../hooks/work-order/useGetCustomFields";

const WorkOrderDetails: React.FC = () => {
  const { translatedMessages } = useContext(TranslationMessagesContext);
  const { workOrderId } = useParams<{ workOrderId: string }>();
  let displayWo = decodeParam(workOrderId);
  const EAMDefaultDate = "0001-01-01T00:00:00";
  const [errorData, setErrorData] = useState("");
  const [showLoading, setShowLoading] = useState(false);

  const [customValErrorState, setCustomValErrorState] = useState(false);
  const [customFieldsErrorState, setCustomFieldsErrorState] = useState(false);
  const isOnline = useNetworkStatusStore((state) => state.isOnline);
  const wasOfflineRef = useRef(false);
  const prevIsOnlineRef = useRef<boolean | null>(null);
  const { trigger } = useGetWorkOrder();
  const { getMetaData } = useGetMetaData();
  const { getCustomValues } = useGetCustomValues();
  const { getCustomFields } = useGetCustomFields();

  //zustand store

  const workOrder = useWorkOrderStore((state) =>
    state.getWorkOrderById(displayWo)
  );
  const addUpdateWorkOrder = useWorkOrderStore(
    (state) => state.addUpdateWorkOrder
  );

  const groups = useWorkOrderStore(
    useShallow((state) => state.getGroupsForWo(displayWo))
  );

  const metadata = useWorkOrderStore((state) =>
    state.getMetaDataForWo(displayWo)
  );

  const customValues = useWorkOrderStore(
    useShallow((state) => state.getUdvListForWo(displayWo))
  );
  const customFields = useWorkOrderStore((state) =>
    state.getCustomElementColumnDetailForWo(displayWo)
  );

  const woToSerial = workOrder?.WriteWoToSerialFlag === true ? "yes" : "no";

  const metadataQuery = useCallback(async () => {
    if (metadata) return; //we have metaData in cache

    const response = await getMetaData(displayWo);
    if (response.isError) {
      setErrorData(response.data?.toString());
    }
  }, [displayWo, getMetaData, metadata]);

  const workOrderQuery = useCallback(async () => {
    if (workOrder) return workOrder; //we have this work order in cache

    const response = await trigger(displayWo);
    if (response.isError) {
      setErrorData(response.data?.toString());
      return undefined;
    } else {
      addUpdateWorkOrder(response.data);
      return response.data;
    }
  }, [addUpdateWorkOrder, displayWo, trigger, workOrder]);

  const customValuesQuery = useCallback(async () => {
    if (customValues) return; //we already have customValues in the cache
    const response = await getCustomValues(displayWo);
    if (response.status !== 200) {
      setCustomValErrorState(true);
    } else if (response.data) {
      setCustomValErrorState(false);
    }
  }, [customValues, displayWo, getCustomValues]);

  const customFieldsQuery = useCallback(
    async (workClass?: string) => {
      if (customFields) return; //we have customFields in the cache
      const subCategory = workClass?.trim() || undefined;
      const response = await getCustomFields(displayWo, subCategory);
      if (response.status !== 200) {
        setCustomFieldsErrorState(true);
      } else if (response.data) {
        setCustomFieldsErrorState(false);
      }
    },
    [customFields, displayWo, getCustomFields]
  );

  const setUpData = useCallback(async () => {
    // Show loading symbol
    setShowLoading(true);
    try {
      const loadedWorkOrder = await workOrderQuery();
      await metadataQuery();
      await customValuesQuery();
      await customFieldsQuery(loadedWorkOrder?.WorkClass);
    } catch (e) {
      setErrorData(String(e));
    } finally {
      setShowLoading(false);
    }
  }, [customFieldsQuery, customValuesQuery, metadataQuery, workOrderQuery]);

  // Track offline transitions so we can re-fetch when internet is restored
  useEffect(() => {
    const prevIsOnline = prevIsOnlineRef.current;
    prevIsOnlineRef.current = isOnline;

    // Skip the first run (initial mount) to avoid duplicating the useIonViewDidEnter load
    if (prevIsOnline === null) return;

    if (!isOnline && prevIsOnline) {
      // Transitioned from online -> offline
      wasOfflineRef.current = true;
      return;
    }
    if (isOnline && wasOfflineRef.current) {
      // Transitioned from offline -> online after a real offline period
      wasOfflineRef.current = false;
      setUpData();
    }
  }, [isOnline, setUpData]);

  useIonViewDidEnter(() => {
    setUpData();
  }, [setUpData]);

  return (
    <>
      <IonHeader>
        <OnlineStatus />
      </IonHeader>
      <ApiError errorData={errorData} />
      <IonLoading
        isOpen={showLoading}
        onDidDismiss={() => setShowLoading(false)}
        duration={5000}
      />
      <>
        {!!metadata &&
          !!workOrder &&
          groups.length > 0 &&
          groups.map((group) => {
            const fields = getFieldsByGroup(metadata, group.GroupName);
            return (
              fields.length > 0 && (
                <IonCard key={group.GroupName + String(group.GroupOrder)}>
                  <IonCardContent>
                    <IonList lines="full">
                      {fields.map((field) => {
                        //The PropertyName of the field is a key of the work order.
                        switch (field.TableName) {
                          case "eq_locations":
                            if (field.PropertyName === "Description") {
                              field.PropertyName = "EquipmentDescription";
                            }
                            break;
                          case "eq_serial":
                            if (field.PropertyName === "Description") {
                              field.PropertyName = "SerialDescription";
                            }
                            break;
                          default:
                            break;
                        }
                        const data =
                          workOrder[field.PropertyName as keyof WorkOrder];
                        if (isEmployeeField(field) && !!data) {
                          return (
                            <EmployeeContactField
                              key={field.IdText}
                              label={field.TranslatedIdText}
                              valueText={formatData(data, field)}
                              employeeId={String(data)}
                            />
                          );
                        }
                        return (
                          <IonItem key={field.IdText}>
                            <IonLabel>
                              <p>{field.TranslatedIdText}</p>
                              {field.NetType === "DateTime" &&
                              data === EAMDefaultDate ? (
                                <></>
                              ) : field.PropertyName ===
                                "WriteWoToSerialFlag" ? (
                                <h3>{woToSerial}</h3>
                              ) : (
                                <h3>{formatData(data, field)}</h3>
                              )}
                            </IonLabel>
                          </IonItem>
                        );
                      })}
                    </IonList>
                  </IonCardContent>
                </IonCard>
              )
            );
          })}
        {/* In the case where there are no groups, show all fields on one card. */}
        {!!metadata && !!workOrder && groups.length === 0 && (
          <IonCard>
            <IonCardContent>
              <IonList lines="full">
                {getAllFields(metadata).map((field) => {
                  const data = workOrder[field.PropertyName as keyof WorkOrder];
                  if (isEmployeeField(field) && !!data) {
                    return (
                      <EmployeeContactField
                        key={field.IdText}
                        label={field.TranslatedIdText}
                        valueText={formatData(data, field)}
                        employeeId={String(data)}
                      />
                    );
                  }
                  return (
                    <IonItem key={field.IdText}>
                      <IonLabel>
                        <p>{field.TranslatedIdText}</p>
                        {field.NetType === "DateTime" &&
                        data === EAMDefaultDate ? (
                          <></>
                        ) : field.PropertyName === "WriteWoToSerialFlag" ? (
                          <h3>{woToSerial}</h3>
                        ) : (
                          <h3>{formatData(data, field)}</h3>
                        )}
                      </IonLabel>
                    </IonItem>
                  );
                })}
              </IonList>
            </IonCardContent>
          </IonCard>
        )}
        {/* Card containing custom fields (with inline edit support) */}
        <WorkOrderCustomFields
          woNumber={displayWo}
          customFields={customFields}
          customValues={customValues}
          customFieldsErrorState={customFieldsErrorState}
          customValErrorState={customValErrorState}
        />
      </>
    </>
  );
};

export default withAITracking(
  reactPlugin,
  WorkOrderDetails,
  "WorkOrderDetails"
);
